import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Layout } from '@/components/Layout';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEditVideo } from '@/hooks/useVideoActions';
import { useVideoById } from '@/hooks/useVideoById';
import { getVideoNip19, isVideoEditable, type VideoData } from '@/lib/videoTypes';

function EditForm({ video }: { video: VideoData }) {
  const navigate = useNavigate();
  const editVideo = useEditVideo();

  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [hashtags, setHashtags] = useState(video.hashtags.join(', '));
  const [cwEnabled, setCwEnabled] = useState(!!video.contentWarning);
  const [cwReason, setCwReason] = useState(video.contentWarning ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  const submit = async () => {
    if (!title.trim() || editVideo.isPending) return;
    const tags = hashtags
      .split(/[,\s]+/)
      .map(t => t.replace(/^#/, '').trim().toLowerCase())
      .filter(Boolean);

    try {
      await editVideo.mutateAsync({
        video,
        input: {
          title: title.trim(),
          description: description.trim(),
          hashtags: tags,
          contentWarning: cwEnabled && cwReason.trim() ? cwReason.trim() : undefined,
          thumbnailUrl: thumbnailUrl.trim() || undefined,
        },
      });
      navigate(`/${getVideoNip19(video)}`);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit video</CardTitle>
        <CardDescription>
          This publishes a new version of the addressable event (same d tag).
          The media URL can't be changed here — re-upload instead if the file
          itself needs to change.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-title">Title</Label>
          <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-description">Description</Label>
          <Textarea id="edit-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-hashtags">Hashtags</Label>
          <Input id="edit-hashtags" value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="nostr, bitcoin, art" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-thumb">Thumbnail URL (optional, replaces current)</Label>
          <Input
            id="edit-thumb"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            placeholder={video.thumbnailUrl ?? 'https://…'}
          />
        </div>

        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="edit-cw" className="flex items-center gap-2 cursor-pointer">
              <TriangleAlert className="h-4 w-4 text-yellow-500" />
              Content warning (NIP-36)
            </Label>
            <Switch id="edit-cw" checked={cwEnabled} onCheckedChange={setCwEnabled} />
          </div>
          {cwEnabled && (
            <Input
              value={cwReason}
              onChange={(e) => setCwReason(e.target.value)}
              placeholder="Reason"
            />
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={submit} disabled={!title.trim() || editVideo.isPending} className="flex-1">
            {editVideo.isPending ? 'Publishing…' : 'Publish new version'}
          </Button>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EditVideoPage({ identifier }: { identifier: string }) {
  const { user } = useCurrentUser();

  let query: { kind?: number; pubkey?: string; identifier?: string } | null = null;
  try {
    const decoded = nip19.decode(identifier);
    if (decoded.type === 'naddr') {
      query = {
        kind: decoded.data.kind,
        pubkey: decoded.data.pubkey,
        identifier: decoded.data.identifier,
      };
    }
  } catch {
    query = null;
  }

  const videoQuery = useVideoById(query);
  const video = videoQuery.data ?? null;

  const [accessDenied, setAccessDenied] = useState<string | null>(null);

  useEffect(() => {
    if (!video) return;
    if (!isVideoEditable(video)) {
      setAccessDenied('This video is a regular event (kind 21/22) and cannot be edited.');
    } else if (user?.pubkey !== video.pubkey) {
      setAccessDenied('You can only edit your own videos.');
    } else {
      setAccessDenied(null);
    }
  }, [video, user]);

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-3 sm:px-6 py-8">
        <h1 className="mb-6 text-2xl font-bold">Edit video</h1>
        {videoQuery.isLoading ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Loading video…
            </CardContent>
          </Card>
        ) : !video ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Video not found on your relays.
            </CardContent>
          </Card>
        ) : accessDenied ? (
          <Alert variant="destructive">
            <TriangleAlert className="h-4 w-4" />
            <AlertDescription>{accessDenied}</AlertDescription>
          </Alert>
        ) : (
          <EditForm video={video} />
        )}
      </div>
    </Layout>
  );
}
